<template>
  <div
    class="atlas-ingestion-group-card"
    :class="{
      'atlas-ingestion-group-card--selected': isSelectable && isSelected,
      'atlas-ingestion-group-card--selectable': isSelectable,
      'atlas-ingestion-group-card--unmatched': isUnmatched,
    }"
    @click="onCardClick"
  >
    <label v-if="isSelectable" class="atlas-ingestion-group-card__select-wrap" @click.stop>
      <input
        type="checkbox"
        class="atlas-ingestion-group-card__checkbox"
        :checked="isSelected"
        :aria-label="`Select group ${(group.name || '').trim() || 'Untitled'}`"
        @change="emit('toggleSelected')"
      />
    </label>
    <div class="atlas-ingestion-group-card__body">
      <div class="atlas-ingestion-group-card__group-header">
        <div class="atlas-ingestion-group-card__group-name-row">
          <template v-if="!isEditingName">
            <div class="global-h4 atlas-ingestion-group-card__group-name-readonly">
              {{ (group.name || "").trim() || "Untitled group" }}
            </div>
            <LinkWithIcon v-if="!uploadingStarted && !isSelectable && !isUnmatched" class="atlas-ingestion-group-card__edit-group-name-link" isNoUnderline @click.stop.prevent="beginEditName">
              <template #icon><IconEmbedded name="pencil_2" :size="16" /></template>
            </LinkWithIcon>
          </template>
          <template v-else>
            <input
              :id="'atlas-group-name-input-' + group.groupId"
              :value="group.name"
              type="text"
              class="atlas-ingestion-group-card__group-card-name-input"
              aria-label="Group name"
              @input="emit('rename', ($event.target as HTMLInputElement).value)"
              @blur="endEditName"
              @keydown.enter.prevent="blurGroupNameInput"
            />
          </template>
        </div>
        <div class="atlas-ingestion-group-card__group-header-trailing">
          <InfoIconWithTooltip v-if="group.rationale && !isSelectable && group.status === 'pending'" class="atlas-ingestion-group-card__rationale-tooltip" :width="200" :iconSize="12">
            <span class="atlas-ingestion-group-card__tooltip-block">{{ group.rationale }}</span>
          </InfoIconWithTooltip>
          <LinkWithIcon v-if="!uploadingStarted && !isSelectable && !isUnmatched" class="atlas-ingestion-group-card__remove-group-link" @click.stop.prevent="emit('skip')">
            <span>Exclude</span>
          </LinkWithIcon>
          <template v-if="isUnmatched">
            <span class="atlas-ingestion-group-card__status-pill atlas-ingestion-group-card__status-pill--unmatched">
              <span>Won&apos;t be uploaded</span>
            </span>
            <SrpButton v-if="!uploadingStarted" class="atlas-ingestion-group-card__promote-btn" size="tiny" color="gray" fill="outlined" @click.stop.prevent="emit('promote')">
              Add to Uploads
            </SrpButton>
          </template>
          <div v-else-if="group.status === 'uploading'" class="atlas-ingestion-group-card__upload-progress" aria-label="Uploading group">
            <div class="atlas-ingestion-group-card__upload-progress-labels">
              <span class="atlas-ingestion-group-card__upload-progress-count"> {{ uploadedFilesEstimate }} / {{ group.filePaths.length }} </span>
              <span class="atlas-ingestion-group-card__upload-progress-percent">{{ clampedUploadPercent }}%</span>
            </div>
            <div class="atlas-ingestion-group-card__upload-progress-track" aria-hidden="true">
              <div class="atlas-ingestion-group-card__upload-progress-fill" :style="{ width: `${clampedUploadPercent}%` }" />
            </div>
          </div>
          <span v-else-if="!isUnmatched && group.status !== 'pending'" class="atlas-ingestion-group-card__status-pill" :class="groupStatusPillModifierClass(group)">
            <span>{{ groupStatusPillLabel(group) }}</span>
          </span>
        </div>
      </div>
      <div v-if="isUnmatched" class="atlas-ingestion-group-card__group-meta-row">
        <p class="global-p atlas-ingestion-group-card__group-summary-text">{{ group.filePaths.length }} photos</p>
      </div>

      <div v-if="!isUnmatched && hasFolderOrFilenamePrefixes(group)" class="atlas-ingestion-group-card__group-preview">
        <div class="atlas-ingestion-group-card__pill-row">
          <span class="atlas-ingestion-group-card__image-count-pill">{{ group.filePaths.length }} photos</span>
          <span
            v-for="prefix in visibleFolderPrefixes(group)"
            :key="group.groupId + ':folder:' + prefix"
            class="atlas-ingestion-group-card__path-pill atlas-ingestion-group-card__path-pill--with-menu"
          >
            <IconEmbedded name="folder_2" :size="14" color="rgba(0, 0, 0, 0.7)" class="atlas-ingestion-group-card__path-pill-icon" />
            <span class="atlas-ingestion-group-card__path-pill-text">{{ displayFolderPrefix(group, prefix) }}</span>
            <button
              v-if="canEditPrefixSources"
              type="button"
              class="atlas-ingestion-group-card__pill-menu-btn"
              :aria-expanded="isSourceMenuOpen('folderPrefix', prefix)"
              aria-haspopup="true"
              aria-label="Source options"
              @click.stop="toggleSourceMenu('folderPrefix', prefix, $event)"
            >
              <IconEmbedded name="caret-bottom_2" :size="10" color="rgba(0, 0, 0, 0.55)" />
            </button>
          </span>
          <span
            v-for="stem in visibleFilenamePrefixes(group)"
            :key="group.groupId + ':filename:' + stem"
            class="atlas-ingestion-group-card__path-pill atlas-ingestion-group-card__path-pill--filename-stem atlas-ingestion-group-card__path-pill--with-menu"
          >
            <IconEmbedded name="image_2" :size="14" color="rgba(0, 0, 0, 0.7)" class="atlas-ingestion-group-card__path-pill-icon" />
            <span class="atlas-ingestion-group-card__path-pill-text">{{ displayFilenamePrefix(group, stem) }}</span>
            <button
              v-if="canEditPrefixSources"
              type="button"
              class="atlas-ingestion-group-card__pill-menu-btn"
              :aria-expanded="isSourceMenuOpen('filenamePrefix', stem)"
              aria-haspopup="true"
              aria-label="Source options"
              @click.stop="toggleSourceMenu('filenamePrefix', stem, $event)"
            >
              <IconEmbedded name="caret-bottom_2" :size="10" color="rgba(0, 0, 0, 0.55)" />
            </button>
          </span>
          <LinkWithIcon
            v-if="totalPrefixSourceCount(group) > groupPreviewLimit"
            class="atlas-ingestion-group-card__show-more-link atlas-ingestion-group-card__show-more-link--inline"
            @click.stop.prevent="emit('togglePreview')"
          >
            <span>{{ isPreviewExpanded ? "show less" : `show all ${totalPrefixSourceCount(group)} sources` }}</span>
          </LinkWithIcon>
        </div>
      </div>

      <div v-else-if="!isUnmatched" class="atlas-ingestion-group-card__group-preview">
        <div class="atlas-ingestion-group-card__pill-row">
          <span class="atlas-ingestion-group-card__image-count-pill">{{ group.filePaths.length }} photos</span>
        </div>
        <template v-for="preview in [formatNoPrefixFilePreview(group)]" :key="group.groupId + preview.title">
          <div class="atlas-ingestion-group-card__pill-row atlas-ingestion-group-card__pill-row--no-prefix-files">
            <span class="atlas-ingestion-group-card__path-pill atlas-ingestion-group-card__path-pill--files">
              <IconEmbedded name="folder_2" :size="14" color="rgba(0, 0, 0, 0.7)" class="atlas-ingestion-group-card__path-pill-icon" />
              <span class="atlas-ingestion-group-card__path-pill-text">{{ preview.display }}</span>
            </span>
            <LinkWithIcon
              v-if="group.filePaths.length > noPrefixFilePreviewLimit"
              class="atlas-ingestion-group-card__show-more-link atlas-ingestion-group-card__show-more-link--inline"
              @click.stop.prevent="emit('togglePreview')"
            >
              <span>{{ isPreviewExpanded ? "show less" : `show all ${group.filePaths.length} files` }}</span>
            </LinkWithIcon>
          </div>
        </template>
      </div>

      <Teleport to="body">
        <div
          v-if="openSourceMenu !== null"
          ref="folderPrefixFloatingEl"
          v-click-outside="closeFolderPrefixMenu"
          :style="folderPrefixFloatingStyles"
          class="atlas-ingestion-group-card__folder-prefix-floating-menu"
          role="menu"
        >
          <button type="button" class="atlas-ingestion-group-card__folder-prefix-menu-item" role="menuitem" :disabled="isOnlyPrefixSource(props.group)" @click.stop="confirmRemovePrefixSource">
            Remove
          </button>
        </div>
      </Teleport>

      <div v-if="group.errorMessage" class="atlas-ingestion-group-card__notice atlas-ingestion-group-card__notice--error">{{ group.errorMessage }}</div>
      <div v-if="showDebug && debugFilePaths.length > 0" class="atlas-ingestion-group-card__debug">
        <div class="atlas-ingestion-group-card__debug-title">Debug file paths</div>
        <div class="atlas-ingestion-group-card__debug-content">
          <span v-for="(entry, index) in debugFilePaths" :key="group.groupId + ':debug:' + index" class="atlas-ingestion-group-card__debug-entry">
            {{ entry }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { autoUpdate, flip, offset, shift, useFloating } from "@floating-ui/vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";
import SrpButton from "@components/ui/SrpButton.vue";
import InfoIconWithTooltip from "@components/ui/InfoIconWithTooltip.vue";
import LinkWithIcon from "@components/LinkWithIcon.vue";
import { normalizeIngestionFolderPrefix } from "@logic/AtlasIngestionGrouping";
import type { ContentIngestionProposedGroup } from "@contracts/contentIngestion";

defineOptions({
  name: "AtlasIngestionGroupCard",
});

export type AtlasIngestionGroupCardStatus = "pending" | "uploading" | "completed" | "error";

export interface AtlasIngestionEditableGroup extends ContentIngestionProposedGroup {
  photoGalleryId: string | null;
  status: AtlasIngestionGroupCardStatus;
  uploadPercent: number;
  errorMessage: string | null;
}

const props = defineProps<{
  group: AtlasIngestionEditableGroup;
  folderLabel: string;
  showDebug: boolean;
  uploadingStarted: boolean;
  isSelectable: boolean;
  isSelected: boolean;
  isPreviewExpanded: boolean;
  /** Unmatched bucket: not part of upload until user promotes. */
  isUnmatched?: boolean;
}>();

const emit = defineEmits<{
  skip: [];
  togglePreview: [];
  toggleSelected: [];
  rename: [name: string];
  promote: [];
  persist: [];
  removePrefixSource: [sourceType: "folderPrefix" | "filenamePrefix", sourceValue: string];
}>();

const groupPreviewLimit = 6;
const noPrefixFilePreviewLimit = 7;

const isEditingName = ref(false);

const canEditPrefixSources = computed(() => !props.uploadingStarted && !props.isSelectable && !props.isUnmatched);

type AtlasIngestionSourceType = "folderPrefix" | "filenamePrefix";
interface AtlasIngestionOpenSourceMenu {
  type: AtlasIngestionSourceType;
  value: string;
}

const openSourceMenu = ref<AtlasIngestionOpenSourceMenu | null>(null);
const folderPrefixReferenceEl = shallowRef<HTMLElement | null>(null);
const folderPrefixFloatingEl = ref<HTMLElement | null>(null);

const { floatingStyles: folderPrefixFloatingStyles } = useFloating(folderPrefixReferenceEl, folderPrefixFloatingEl, {
  placement: "top-start",
  middleware: [offset(6), flip(), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
});

function isSourceMenuOpen(sourceType: AtlasIngestionSourceType, sourceValue: string): boolean {
  return openSourceMenu.value?.type === sourceType && openSourceMenu.value?.value === sourceValue;
}

function toggleSourceMenu(sourceType: AtlasIngestionSourceType, sourceValue: string, event: MouseEvent) {
  const target = event.currentTarget;
  if (!(target instanceof HTMLElement)) {
    return;
  }
  if (isSourceMenuOpen(sourceType, sourceValue)) {
    closeFolderPrefixMenu();
    return;
  }
  openSourceMenu.value = {
    type: sourceType,
    value: sourceValue,
  };
  folderPrefixReferenceEl.value = target;
}

function closeFolderPrefixMenu() {
  openSourceMenu.value = null;
  folderPrefixReferenceEl.value = null;
}

function isOnlyPrefixSource(group: AtlasIngestionEditableGroup): boolean {
  return totalPrefixSourceCount(group) <= 1;
}

function confirmRemovePrefixSource() {
  const source = openSourceMenu.value;
  if (source == null || isOnlyPrefixSource(props.group)) {
    closeFolderPrefixMenu();
    return;
  }
  emit("removePrefixSource", source.type, source.value);
  closeFolderPrefixMenu();
}

function onDocumentKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") {
    closeFolderPrefixMenu();
  }
}

watch(openSourceMenu, v => {
  if (v != null) {
    window.addEventListener("keydown", onDocumentKeydown, true);
  } else {
    window.removeEventListener("keydown", onDocumentKeydown, true);
  }
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", onDocumentKeydown, true);
});

const clampedUploadPercent = computed(() => Math.min(100, Math.max(0, Math.round(props.group.uploadPercent || 0))));

/** Estimated count of completed file uploads, derived from upload percent (we don't get a per-file count here). */
const uploadedFilesEstimate = computed(() => {
  const total = props.group.filePaths.length;
  if (total <= 0) {
    return 0;
  }
  return Math.min(total, Math.round((clampedUploadPercent.value / 100) * total));
});

const debugFilePaths = computed(() => props.group.filePaths.map((path, index) => `${index + 1}. ${path}`));

function onCardClick() {
  if (props.isSelectable) {
    emit("toggleSelected");
  }
}

function beginEditName() {
  isEditingName.value = true;
  void nextTick(() => {
    const el = document.getElementById(`atlas-group-name-input-${props.group.groupId}`) as HTMLInputElement | null;
    el?.focus();
    el?.select();
  });
}

function endEditName() {
  emit("persist");
  isEditingName.value = false;
}

function blurGroupNameInput(event: KeyboardEvent) {
  const t = event.target;
  if (t instanceof HTMLInputElement) {
    t.blur();
  }
}

function hasFolderOrFilenamePrefixes(group: AtlasIngestionEditableGroup): boolean {
  return (group.folderPrefixes ?? []).length > 0 || (group.filenamePrefixes ?? []).length > 0;
}

function totalPrefixSourceCount(group: AtlasIngestionEditableGroup): number {
  return (group.folderPrefixes ?? []).length + (group.filenamePrefixes ?? []).length;
}

function sortedFolderPrefixes(group: AtlasIngestionEditableGroup): string[] {
  return [...(group.folderPrefixes ?? [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

function sortedFilenamePrefixes(group: AtlasIngestionEditableGroup): string[] {
  return [...(group.filenamePrefixes ?? [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
}

/** When collapsed, show up to `groupPreviewLimit` pills total (folders first, then filename stems). */
function cappedFolderAndFilenamePrefixLists(group: AtlasIngestionEditableGroup): { folders: string[]; stems: string[] } {
  const folders = sortedFolderPrefixes(group);
  const stems = sortedFilenamePrefixes(group);
  if (props.isPreviewExpanded) {
    return { folders, stems };
  }
  const max = groupPreviewLimit;
  const folderTake = Math.min(folders.length, max);
  const stemTake = Math.min(stems.length, Math.max(0, max - folderTake));
  return { folders: folders.slice(0, folderTake), stems: stems.slice(0, stemTake) };
}

function visibleFolderPrefixes(group: AtlasIngestionEditableGroup): string[] {
  return cappedFolderAndFilenamePrefixLists(group).folders;
}

function visibleFilenamePrefixes(group: AtlasIngestionEditableGroup): string[] {
  return cappedFolderAndFilenamePrefixLists(group).stems;
}

function visibleNoPrefixFilePaths(group: AtlasIngestionEditableGroup): string[] {
  const paths = [...group.filePaths].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  if (props.isPreviewExpanded) {
    return paths;
  }
  return paths.slice(0, noPrefixFilePreviewLimit);
}

function fileNameFromRelativePath(relativePath: string): string {
  const norm = relativePath.replace(/\\/g, "/").trim();
  const parts = norm.split("/").filter(Boolean);
  const last = parts.length > 0 ? parts[parts.length - 1] : undefined;
  return last ?? relativePath;
}

function formatNoPrefixFilePreview(group: AtlasIngestionEditableGroup): { display: string; title: string } {
  const paths = visibleNoPrefixFilePaths(group);
  return {
    display: paths.map(fileNameFromRelativePath).join(", "),
    title: paths.map(p => p.replace(/\\/g, "/")).join(", "),
  };
}

function sharedRootFolderPrefixForGroup(group: AtlasIngestionEditableGroup): string {
  const paths = [...(group.folderPrefixes ?? [])]
    .map(p => normalizeIngestionFolderPrefix(p))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  if (paths.length === 0) {
    return "";
  }
  const labelRoot = normalizeIngestionFolderPrefix(props.folderLabel);
  if (paths.length === 1) {
    const only = paths[0];
    if (labelRoot) {
      if (only.toLowerCase() === labelRoot.toLowerCase()) {
        return labelRoot;
      }
      if (only.toLowerCase().startsWith(`${labelRoot.toLowerCase()}/`)) {
        return labelRoot;
      }
    }
    return "";
  }
  const segments = paths.map(p => p.split("/").filter(Boolean));
  const minLen = Math.min(...segments.map(s => s.length));
  const common: string[] = [];
  for (let i = 0; i < minLen; i++) {
    const seg0 = segments[0][i];
    if (segments.every(s => s[i].toLowerCase() === seg0.toLowerCase())) {
      common.push(seg0);
    } else {
      break;
    }
  }
  return common.join("/");
}

function displayFolderPrefix(group: AtlasIngestionEditableGroup, rawPrefix: string): string {
  const normalized = normalizeIngestionFolderPrefix(rawPrefix);
  const root = sharedRootFolderPrefixForGroup(group);
  let rest = normalized;
  if (root) {
    if (normalized.toLowerCase() === root.toLowerCase()) {
      rest = "";
    } else if (normalized.toLowerCase().startsWith(`${root.toLowerCase()}/`)) {
      rest = normalized.slice(root.length + 1);
    }
  }
  if (!rest) {
    return "\\";
  }
  return `\\${rest.replace(/\//g, "\\")}`;
}

function sharedRootFilenamePrefixForGroup(group: AtlasIngestionEditableGroup): string {
  const paths = [...(group.filenamePrefixes ?? [])]
    .map(p => normalizeIngestionFolderPrefix(p))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  if (paths.length === 0) {
    return "";
  }
  const labelRoot = normalizeIngestionFolderPrefix(props.folderLabel);
  if (paths.length === 1) {
    const only = paths[0];
    if (labelRoot) {
      if (only.toLowerCase() === labelRoot.toLowerCase()) {
        return labelRoot;
      }
      if (only.toLowerCase().startsWith(`${labelRoot.toLowerCase()}/`)) {
        return labelRoot;
      }
    }
    return "";
  }
  const segments = paths.map(p => p.split("/").filter(Boolean));
  const minLen = Math.min(...segments.map(s => s.length));
  const common: string[] = [];
  for (let i = 0; i < minLen; i++) {
    const seg0 = segments[0][i];
    if (segments.every(s => s[i].toLowerCase() === seg0.toLowerCase())) {
      common.push(seg0);
    } else {
      break;
    }
  }
  return common.join("/");
}

function displayFilenamePrefix(group: AtlasIngestionEditableGroup, rawStem: string): string {
  const normalized = normalizeIngestionFolderPrefix(rawStem);
  const root = sharedRootFilenamePrefixForGroup(group);
  let rest = normalized;
  if (root) {
    if (normalized.toLowerCase() === root.toLowerCase()) {
      rest = "";
    } else if (normalized.toLowerCase().startsWith(`${root.toLowerCase()}/`)) {
      rest = normalized.slice(root.length + 1);
    }
  }
  if (!rest) {
    return "\\";
  }
  return `\\${rest.replace(/\//g, "\\")}`;
}

function groupStatusPillModifierClass(group: AtlasIngestionEditableGroup): string {
  if (group.status === "completed") {
    return "atlas-ingestion-group-card__status-pill--completed";
  }
  if (group.status === "error") {
    return "atlas-ingestion-group-card__status-pill--error";
  }
  return "atlas-ingestion-group-card__status-pill--pending";
}

function groupStatusPillLabel(group: AtlasIngestionEditableGroup): string {
  if (group.status === "completed") {
    return "Complete";
  }
  if (group.status === "error") {
    return "Failed";
  }
  return "Pending";
}
</script>

<style scoped lang="scss">
.atlas-ingestion-group-card {
  margin-top: 14px;
  padding: 12px;
  border: 1px solid darkgray;
  border-radius: 6px;
  background: #fff;
  display: flex;
  gap: 12px;
  align-items: flex-start;

  &--unmatched {
    border-style: dashed;
    border-color: rgba(0, 0, 0, 0.35);
    background: rgba(0, 0, 0, 0.02);
  }

  &--selectable {
    cursor: pointer;
  }

  &--selected {
    border-color: var(--srp-color-blue, #2d6cdf);
    box-shadow: 0 0 0 1px rgba(45, 108, 223, 0.2);
    background: rgba(45, 108, 223, 0.03);
  }

  &__select-wrap {
    flex-shrink: 0;
    padding-top: 2px;
    cursor: pointer;
    display: inline-flex;
    width: auto;
    align-items: center;
    justify-content: center;
  }

  &__checkbox {
    width: 18px;
    height: 18px;
    padding: 0;
    margin: 0;
    display: inline-block;
    cursor: pointer;
    box-sizing: border-box;
    flex-shrink: 0;
  }

  &__body {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__group-header {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    align-items: center;
  }

  &__group-header-trailing {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
  }

  &__group-name-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__group-name-readonly {
    flex: 0 1 auto;
    min-width: 0;
  }

  &__edit-group-name-link {
    flex-shrink: 0;
    font-size: 13px;
  }

  &__group-card-name-input {
    flex: 1 1 auto;
    min-width: 180px;
    max-width: 520px;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid var(--srp-color-border-subtle, #ccc);
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 19px;
    font-weight: 700;
    font-family: inherit;
    line-height: 23px;
  }

  &__remove-group-link {
    font-size: 13px;
  }

  &__image-count-pill {
    display: inline-flex;
    align-items: center;
    padding: 5px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.25;
    color: #0d4a4b;
    background: rgba(17, 134, 137, 0.14);
    border: 1px solid #b8d8e8;
    white-space: nowrap;
  }

  &__pill-menu-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: -2px 6px -2px 2px;
    padding: 6px;
    border: none;
    border-radius: 4px;
    background: transparent;
    cursor: pointer;
    line-height: 0;

    &:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  &__folder-prefix-floating-menu {
    z-index: 10050;
    min-width: 120px;
    padding: 4px 0;
    border-radius: 6px;
    background: white;
    border: 1px solid rgba(0, 0, 0, 0.12);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  }

  &__folder-prefix-menu-item {
    display: block;
    width: 100%;
    padding: 7px 12px;
    border: none;
    background: transparent;
    font-size: 12px;
    line-height: 1.25;
    text-align: left;
    cursor: pointer;
    color: #1a1a1a;

    &:hover:not(:disabled) {
      background: rgba(0, 0, 0, 0.05);
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  &__group-meta-row {
    margin-top: 6px;
    margin-bottom: 6px;
    display: flex;
    align-items: baseline;
    gap: 12px;
    flex-wrap: wrap;
  }

  &__group-summary-text {
    margin: 0;
    font: 700 13px/1.45 sans-serif;
    color: rgba(0, 0, 0, 0.897);
  }

  &__upload-progress {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 160px;
    font-variant-numeric: tabular-nums;
    font-feature-settings: "tnum";
  }

  &__upload-progress-labels {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    font-size: 12px;
    line-height: 1.2;
  }

  &__upload-progress-count {
    color: #495057;
    font-weight: 600;
  }

  &__upload-progress-percent {
    color: #0a58ca;
    font-weight: 700;
  }

  &__upload-progress-track {
    height: 6px;
    border-radius: 999px;
    background: rgba(10, 88, 202, 0.15);
    overflow: hidden;
  }

  &__upload-progress-fill {
    height: 100%;
    border-radius: 999px;
    background: #0a58ca;
    transition: width 0.25s ease-out;
    min-width: 0;
  }

  &__status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    white-space: nowrap;
  }

  &__status-pill--pending {
    background: #e9ecef;
    color: #495057;
  }

  &__status-pill--unmatched {
    background: #fff3cd;
    color: #856404;
  }

  &__promote-btn {
    flex-shrink: 0;
  }

  &__status-pill--completed {
    background: #d1e7dd;
    color: #0f5132;
  }

  &__status-pill--error {
    background: #f8d7da;
    color: #842029;
  }

  &__group-preview {
    margin-top: 8px;
    margin-left: 0;
    font: 400 13px/1.45 sans-serif;
    color: var(--srp-color-text-muted, #666);
  }

  &__group-preview-name {
    word-break: break-word;
  }

  &__group-preview-no-prefix {
    font-weight: 400;
    word-break: break-word;
  }

  &__pill-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    &--no-prefix-files {
      margin-top: 6px;
    }
  }

  &__path-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 4px 8px 4px 10px;
    border-radius: 6px;
    background: #f1f3f5;
    color: var(--fg-muted, #555);
    border: 1px solid rgba(0, 0, 0, 0.06);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    white-space: nowrap;

    &--with-menu {
      padding-right: 2px;
    }
  }

  &__path-pill-icon {
    margin-top: -1px;
    flex-shrink: 0;
    color: var(--fg-muted, #888);
  }

  &__path-pill-text {
    min-width: 0;
  }

  &__path-pill--files {
    align-items: center;
    white-space: normal;
    word-break: break-word;

    .atlas-ingestion-group-card__path-pill-text {
      word-break: break-word;
    }
  }

  &__show-more-link {
    margin-top: 6px;
    font-size: 13px;

    &--inline {
      display: inline-flex;
      margin-top: 0;
      margin-left: 12px;
      vertical-align: baseline;
    }
  }

  &__rationale-tooltip {
    display: inline-flex;
    vertical-align: middle;
    flex-shrink: 0;
  }

  &__tooltip-block {
    display: block;
    font-size: 13px;
    line-height: 1.45;
    white-space: pre-wrap;
  }

  &__notice {
    margin-top: 14px;
    padding: 10px 12px;
    border-radius: 6px;
    font-size: 14px;

    &--error {
      background: rgba(220, 53, 69, 0.16);
    }
  }

  &__debug {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px dashed rgba(0, 0, 0, 0.18);
  }

  &__debug-title {
    font-size: 12px;
    font-weight: 700;
    margin-bottom: 4px;
    color: rgba(0, 0, 0, 0.8);
  }

  &__debug-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    font-size: 12px;
    line-height: 1.4;
    color: var(--srp-color-text-muted, #666);
  }

  &__debug-entry {
    display: inline-block;
    white-space: nowrap;
    word-break: normal;
  }
}
</style>
